import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { CmsLink as Link, EditableText } from '../content/EditableText';
import { useCmsContent } from '../content/CmsContentContext';

const Home = () => {
  const { content } = useCmsContent();
  const { services, specialties, testimonials, insuranceProviders } = content;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-brand-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Image Collage */}
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <img
                  src="https://images.unsplash.com/photo-1586752002496-cd05ccef47f3?w=200&h=200&fit=crop"
                  alt={content.pages.home.therapistAlt}
                  className="rounded-lg w-full h-32 object-cover"
                />
                <div className="bg-brand-700 rounded-lg h-32"></div>
                <img
                  src="https://images.unsplash.com/photo-1573495804664-b1c0849525af?w=200&h=200&fit=crop"
                  alt={content.pages.home.therapistAlt}
                  className="rounded-lg w-full h-32 object-cover"
                />
              </div>
              <div className="space-y-2 mt-8">
                <img
                  src="https://images.unsplash.com/photo-1714976694810-85add1a29c96?w=200&h=200&fit=crop"
                  alt={content.pages.home.therapistAlt}
                  className="rounded-lg w-full h-32 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1592947945242-69312358628b?w=200&h=200&fit=crop"
                  alt={content.pages.home.therapistAlt}
                  className="rounded-lg w-full h-32 object-cover"
                />
                <div className="bg-gray-700 rounded-lg h-32"></div>
              </div>
              <div className="space-y-2">
                <div className="bg-brand-700 rounded-lg h-32"></div>
                <img
                  src="https://images.unsplash.com/photo-1714976694867-bc0e012fab70?w=200&h=200&fit=crop"
                  alt={content.pages.home.therapySessionAlt}
                  className="rounded-lg w-full h-32 object-cover"
                />
                <div className="bg-brand-700 rounded-lg h-32"></div>
              </div>
            </div>

            {/* Right: Text Content */}
            <div>
              <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
                <EditableText contentKey="pages.home.heroTitle" />
              </h1>
              <p className="text-lg text-gray-700 mb-4">
                <EditableText contentKey="pages.home.heroIntro" />
              </p>
              <p className="text-lg text-gray-700 mb-8">
                <EditableText contentKey="pages.home.heroBody" />
              </p>
              <Link
                to="/book-appointment"
                className="inline-flex items-center px-8 py-3 bg-brand-700 text-white rounded-md hover:bg-brand-800 transition-colors text-lg font-medium"
              >
                <EditableText contentKey="pages.home.heroButton" multiline={false} /> <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            <EditableText contentKey="pages.home.servicesHeading" />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, serviceIndex) => (
              <div key={service.id} className="group">
                <div className="overflow-hidden rounded-lg mb-4">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3"><EditableText contentKey={`services.${serviceIndex}.title`} /></h3>
                <p className="text-gray-600 mb-4"><EditableText contentKey={`services.${serviceIndex}.description`} /></p>
                <Link
                  to={`/services/${service.id}`}
                  className="inline-flex items-center text-brand-700 hover:text-brand-800 font-medium"
                >
                  <EditableText contentKey="pages.home.readMore" multiline={false} /> <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-12"><EditableText contentKey="pages.home.insuranceHeading" /></h2>
          <div className="flex flex-wrap justify-center items-center gap-6">
            {insuranceProviders.map((provider, index) => (
              <div
                key={index}
                className="bg-white px-8 py-6 rounded-lg shadow-sm hover:shadow-md transition-shadow min-w-[200px] border-2 border-gray-100"
              >
                <p className="text-lg font-semibold text-gray-800"><EditableText contentKey={`insuranceProviders.${index}`} multiline={false} /></p>
              </div>
            ))}
          </div>
          <Link
            to="/insurance"
            className="inline-block mt-8 text-brand-700 hover:text-brand-800 font-medium"
          >
            <EditableText contentKey="pages.home.insuranceLink" multiline={false} /> <ArrowRight className="inline ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-light text-gray-900 mb-6"><EditableText contentKey="pages.home.ctaTitle" /></h2>
              <p className="text-lg text-gray-700 mb-8">
                <EditableText contentKey="pages.home.ctaBody" />
              </p>
              <Link
                to="/book-appointment"
                className="inline-flex items-center px-8 py-3 bg-brand-700 text-white rounded-md hover:bg-brand-800 transition-colors text-lg font-medium"
              >
                <EditableText contentKey="pages.home.ctaButton" multiline={false} /> <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            <div className="overflow-hidden rounded-lg">
              <img
                src="https://images.pexels.com/photos/5473177/pexels-photo-5473177.jpeg"
                alt={content.pages.home.therapySessionAlt}
                className="w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Specialty Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white relative">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1518495973542-4542c06a5843)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">
              <EditableText contentKey="pages.home.specialtyHeading" />
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto">
              <EditableText contentKey="pages.home.specialtyBody" />
            </p>
          </div>
        </div>
      </section>

      {/* Areas We Can Help With */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            <EditableText contentKey="pages.home.areasHeading" />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specialties.map((specialty, specialtyIndex) => (
              <Link
                key={specialty.id}
                to={`/specialty/${specialty.id}`}
                className="group overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="overflow-hidden">
                  <img
                    src={specialty.image}
                    alt={specialty.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4 bg-white">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2"><EditableText contentKey={`specialties.${specialtyIndex}.title`} /></h3>
                  <p className="text-sm text-gray-600 line-clamp-3"><EditableText contentKey={`specialties.${specialtyIndex}.description`} /></p>
                  <span className="inline-flex items-center text-brand-700 hover:text-brand-800 font-medium mt-2 text-sm">
                    <EditableText contentKey="pages.home.readMore" multiline={false} /> <ArrowRight className="ml-1 h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-brand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12"><EditableText contentKey="pages.home.testimonialsHeading" /></h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, testimonialIndex) => (
              <div key={testimonial.id} className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <CheckCircle key={i} className="h-5 w-5 text-brand-700" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"<EditableText contentKey={`testimonials.${testimonialIndex}.text`} />"</p>
                <p className="text-gray-900 font-semibold"><EditableText contentKey={`testimonials.${testimonialIndex}.name`} multiline={false} /></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="overflow-hidden rounded-lg">
              <img
                src="https://images.unsplash.com/photo-1600427652630-f97cc4db10cd"
                alt={content.pages.home.familyTherapyAlt}
                className="w-full h-96 object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-light text-gray-900 mb-6"><EditableText contentKey="pages.home.ctaTitle" /></h2>
              <p className="text-lg text-gray-700 mb-8">
                <EditableText contentKey="pages.home.ctaBody" />
              </p>
              <Link
                to="/book-appointment"
                className="inline-flex items-center px-8 py-3 bg-brand-700 text-white rounded-md hover:bg-brand-800 transition-colors text-lg font-medium"
              >
                <EditableText contentKey="pages.home.ctaButton" multiline={false} /> <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
